import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Globe, Building2, MapPin, TrendingUp, ExternalLink, Cpu, Trophy, Sparkles, Loader2 } from "lucide-react";
import { getReport, getReportStatus, getEnhancementStatus } from "@/lib/api";
import { logReportDownload, isQuotaError, quotaErrorPayload } from "@/lib/subscriptionApi";
import { useSubscription } from "@/contexts/SubscriptionContext";
import UpgradeModal from "@/components/UpgradeModal";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import ReportSidebar, { SECTION_LIST } from "@/components/report/ReportSidebar";
import { SectionTitle, Card, KV, Pill, BulletList, DataTable, Subgrid, SubCard } from "@/components/report/atoms";
import AccountScoreGauge from "@/components/report/AccountScoreGauge";
import CXOEmail from "@/components/report/CXOEmail";
import PPTStoryline from "@/components/report/PPTStoryline";
import PipelineTracker from "@/components/report/PipelineTracker";
import EnhanceDialog from "@/components/report/EnhanceDialog";
import EnhancementSection from "@/components/report/EnhancementSection";
import RadarLoader from "@/components/RadarLoader";
import { Button } from "@/components/ui/button";

const Section = ({ id, index, title, sub, children }) => (
  <section id={id} className="section-reveal print-section scroll-mt-20 mb-14" data-testid={`section-${id}`}>
    <SectionTitle id={id} index={index} title={title} sub={sub} />
    <div className="space-y-4">{children}</div>
  </section>
);

export default function ReportPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | pending | completed | failed | not_found
  const [errMsg, setErrMsg] = useState(null);
  const [activeId, setActiveId] = useState("s1");
  const [enhanceOpen, setEnhanceOpen] = useState(false);
  const [upgrade, setUpgrade] = useState(null);
  const { refresh: refreshSub } = useSubscription();
  const observerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const enhPollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const stopPoll = () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const loadFull = async () => {
      try {
        const d = await getReport(id);
        if (cancelled) return;
        setData(d);
        // legacy reports without status field — treat as completed if report present
        const s = d.status || (d.report ? "completed" : "pending");
        setStatus(s);
        if (s === "failed") setErrMsg(d.error || "Generation failed");
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setStatus("not_found");
        } else {
          toast.error("Could not load report", { description: e?.response?.data?.detail || e.message });
          setStatus("failed");
          setErrMsg(e.message);
        }
      }
    };

    const poll = async () => {
      try {
        const s = await getReportStatus(id);
        if (cancelled) return;
        if (s.status === "completed") {
          await loadFull();
        } else if (s.status === "failed") {
          setStatus("failed");
          setErrMsg(s.error || "Generation failed");
          toast.error("Generation failed", { description: s.error });
        } else {
          // still pending — schedule next poll
          pollTimerRef.current = setTimeout(poll, 3000);
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          setStatus("not_found");
        } else {
          // transient — retry
          pollTimerRef.current = setTimeout(poll, 5000);
        }
      }
    };

    setStatus("loading");
    setData(null);
    setErrMsg(null);
    loadFull().then(() => {
      // After initial load, if still pending kick off polling
      // We re-check via state by reading from the just-set value isn't clean; instead use a status check
      // Use a microtask to grab current status
      setTimeout(() => {
        // If status is pending start poll
        // We can't read state from here reliably; just always start poll if no report and not failed/notfound.
      }, 0);
    });

    // Independent poll trigger: kick off after 1s if no completed report
    const initialKick = setTimeout(() => {
      if (cancelled) return;
      poll();
    }, 1500);

    return () => {
      cancelled = true;
      clearTimeout(initialKick);
      stopPoll();
    };
  }, [id]);

  useEffect(() => {
    if (!data || !data.report) return;
    if (observerRef.current) observerRef.current.disconnect();
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -65% 0px", threshold: 0 }
    );
    SECTION_LIST.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    observerRef.current = obs;
    return () => obs.disconnect();
  }, [data]);

  const onExportPDF = async () => {
    try {
      await logReportDownload(id);
      refreshSub();
      // Small delay so the server can record the download and the badge updates
      // before the print dialog grabs the window
      setTimeout(() => window.print(), 50);
    } catch (err) {
      if (isQuotaError(err)) {
        setUpgrade(quotaErrorPayload(err));
      } else {
        toast.error("Could not start export", { description: err?.response?.data?.detail || err.message });
      }
    }
  };

  // Poll enhancement status if pending
  useEffect(() => {
    const enh = data?.enhancement;
    if (!enh || enh.status !== "pending") {
      if (enhPollRef.current) {
        clearTimeout(enhPollRef.current);
        enhPollRef.current = null;
      }
      return;
    }
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await getEnhancementStatus(id);
        if (cancelled) return;
        if (s.status === "completed" || s.status === "failed") {
          // refetch full doc
          try {
            const d = await getReport(id);
            if (!cancelled) setData(d);
            if (s.status === "failed") {
              toast.error("Enhancement failed", { description: s.error || "LLM error" });
            } else {
              toast.success("Enhancement ready");
            }
          } catch (e) {
            console.warn("[report] failed to refetch after enhancement update:", e?.message || e);
          }
        } else {
          enhPollRef.current = setTimeout(tick, 4000);
        }
      } catch {
        enhPollRef.current = setTimeout(tick, 6000);
      }
    };
    enhPollRef.current = setTimeout(tick, 2500);
    return () => {
      cancelled = true;
      if (enhPollRef.current) {
        clearTimeout(enhPollRef.current);
        enhPollRef.current = null;
      }
    };
  }, [data, id]);

  const onEnhanceStarted = async () => {
    // Optimistically mark enhancement pending so the UI shows the loader + polling kicks in
    try {
      const d = await getReport(id);
      setData(d);
    } catch (e) {
      console.warn("[report] could not refetch after enhance kicked off:", e?.message || e);
    }
  };

  if (status === "loading" || status === "pending" || (status === "completed" && (!data || !data.report))) {
    return (
      <div className="min-h-screen radar-grid-bg">
        <AppHeader />
        <RadarLoader company={data?.company_name} />
      </div>
    );
  }

  if (status === "not_found") {
    return (
      <div className="min-h-screen radar-grid-bg">
        <AppHeader />
        <div className="max-w-4xl mx-auto p-12 text-center">
          <div className="kpi-label mb-2">/ NOT FOUND</div>
          <h1 className="font-display text-3xl text-white">Report not found</h1>
          <Link to="/" className="inline-block mt-6 text-[#0A84FF] hover:underline" data-testid="back-home-link">← Back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="min-h-screen radar-grid-bg">
        <AppHeader />
        <div className="max-w-4xl mx-auto p-12 text-center" data-testid="report-error">
          <div className="kpi-label text-[#FF453A] mb-2">/ GENERATION FAILED</div>
          <h1 className="font-display text-3xl text-white">{data?.company_name || "Report"}</h1>
          <p className="text-sm text-[#A3A3A3] mt-4 max-w-xl mx-auto">{errMsg || "The intelligence engine encountered an error compiling this account."}</p>
          <Link to="/" className="inline-block mt-6 text-[#0A84FF] hover:underline" data-testid="back-home-link">← Back to dashboard</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen radar-grid-bg">
        <AppHeader />
        <div className="max-w-4xl mx-auto p-12 text-center">
          <div className="kpi-label mb-2">/ NOT FOUND</div>
          <h1 className="font-display text-3xl text-white">Report not found</h1>
          <Link to="/" className="inline-block mt-6 text-[#0A84FF] hover:underline" data-testid="back-home-link">← Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const r = data.report || {};
  const exec = r.executive_snapshot || {};
  const co = r.company_overview || {};
  const bs = r.business_structure || {};
  const gf = r.global_footprint || {};
  const fin = r.financial_insights || {};
  const sp = r.strategic_priorities || {};
  const tech = r.technology_landscape || [];
  const comp = r.competitor_landscape || {};
  const part = r.partnerships || {};
  const ma = r.mergers_acquisitions || [];
  const sus = r.sustainability || {};
  const ch = r.challenges || {};
  const out = r.outsourcing || {};
  const lead = r.leadership || [];
  const kpi = r.kpi_focus || {};
  const score = r.attractiveness_score || {};
  const deals = r.deal_opportunities || [];
  const email = r.cxo_email || {};
  const ppt = r.ppt_storyline || [];
  const pipe = r.pipeline_tracker || {};
  const conf = r.data_confidence || {};
  const web = data.web_research || {};
  const webSources = (web.sources || []).filter(s => s && s.url);
  const grouped = webSources.reduce((acc, s) => {
    const k = s.dimension || "other";
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});
  const DIM_LABELS = {
    profile: "Profile & Financials",
    leadership: "Leadership & Org",
    news: "Recent News",
    technology: "Technology & Products",
    ma: "M&A / Partnerships",
    other: "Other",
  };

  const headerRight = (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        onClick={() => setEnhanceOpen(true)}
        data-testid="enhance-btn"
        className="h-9 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white gap-2 transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5"/>
        <span className="text-xs font-mono-data tracking-wider uppercase">
          {data?.enhancement?.status === "completed" ? "Re-enhance" : "Enhance"}
        </span>
      </Button>
      <Button
        type="button"
        onClick={onExportPDF}
        data-testid="export-pdf-btn"
        className="h-9 rounded-sm bg-transparent hover:bg-[#1F1F1F] text-white border border-[#262626] hover:border-[#404040] gap-2 transition-colors"
      >
        <Download className="w-3.5 h-3.5"/>
        <span className="text-xs font-mono-data tracking-wider uppercase">Export PDF</span>
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen radar-grid-bg">
      <AppHeader rightSlot={headerRight} />

      {/* Hero strip */}
      <div className="border-b border-[#262626] bg-[#0A0A0A]/60 backdrop-blur no-print">
        <div className="max-w-[1600px] mx-auto px-6 md:px-8 py-6 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-[12px] text-[#737373] hover:text-white transition-colors mb-3 font-mono-data tracking-wider uppercase" data-testid="back-link">
              <ArrowLeft className="w-3 h-3"/> Dashboard
            </Link>
            <div className="kpi-label mb-1">/ ACCOUNT INTELLIGENCE REPORT</div>
            <h1 className="font-display text-3xl md:text-5xl tracking-tighter text-white" data-testid="report-company-name">
              {data.company_name}
            </h1>
            <div className="flex flex-wrap gap-2 mt-3">
              {data.geography && <Pill tone="blue">{data.geography}</Pill>}
              {data.industry && <Pill tone="neutral">{data.industry}</Pill>}
              {conf.confidence_level && <Pill tone={conf.confidence_level === "High" ? "green" : conf.confidence_level === "Low" ? "red" : "amber"}>Confidence · {conf.confidence_level}</Pill>}
              {web.grounded && <Pill tone="blue" data-testid="web-grounded-pill">Web-grounded · {webSources.length} sources</Pill>}
            </div>
          </div>
          <div className="text-right hidden md:block">
            <div className="kpi-label">Generated</div>
            <div className="font-mono-data text-sm text-white mt-1">{new Date(data.created_at).toLocaleString()}</div>
            {score.score != null && (
              <div className="mt-3">
                <div className="kpi-label">Attractiveness</div>
                <div className="font-mono-data text-3xl text-[#0A84FF] tracking-tight">{Number(score.score).toFixed(1)}<span className="text-[#525252]">/10</span></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body grid: sidebar + content */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-8 grid grid-cols-12 gap-6">
        <aside className="hidden lg:block lg:col-span-3 xl:col-span-3">
          <ReportSidebar activeId={activeId} />
        </aside>

        <main className="col-span-12 lg:col-span-9 xl:col-span-9 py-8">
          {/* Enhancement banner / panel */}
          {data?.enhancement?.status === "pending" && (
            <div className="mb-8 bg-[#0A84FF]/10 border border-[#0A84FF]/40 rounded-sm p-5 flex items-center gap-4 no-print" data-testid="enhancement-pending-banner">
              <Loader2 className="w-5 h-5 text-[#0A84FF] animate-spin" strokeWidth={1.8}/>
              <div className="flex-1">
                <div className="kpi-label text-[#0A84FF]">/ PRIMARY DATA AMPLIFICATION · COMPILING</div>
                <div className="text-sm text-white mt-0.5">Overlaying your internal knowledge — typically 60-90 seconds.</div>
              </div>
            </div>
          )}
          {data?.enhancement?.status === "failed" && (() => {
            const kind = data.enhancement.error_kind || "unknown";
            const TITLES = {
              upstream_unavailable: "LLM PROVIDER TEMPORARILY UNAVAILABLE",
              budget_exhausted: "LLM CREDIT BUDGET EXHAUSTED",
              rate_limited: "LLM PROVIDER RATE-LIMITING",
              timeout: "LLM REQUEST TIMED OUT",
              unknown: "ENHANCEMENT FAILED",
            };
            const HINTS = {
              upstream_unavailable: "Anthropic/OpenAI returned a 502. This is usually transient — click Re-enhance in 30-60 seconds.",
              budget_exhausted: "Top up your Emergent Universal Key balance: Profile → Universal Key → Add Balance.",
              rate_limited: "Too many requests in a short window. Wait ~60 seconds and click Re-enhance.",
              timeout: "The request took too long. Click Re-enhance to try again.",
              unknown: "An unexpected error occurred. Click Re-enhance to retry.",
            };
            return (
              <div className="mb-8 bg-[#FF453A]/10 border border-[#FF453A]/40 rounded-sm p-5 no-print" data-testid="enhancement-failed-banner">
                <div className="kpi-label text-[#FF453A] mb-1">/ {TITLES[kind]}</div>
                <div className="text-sm text-white">{data.enhancement.error || "Unknown error"}</div>
                <div className="text-xs text-[#A3A3A3] mt-1.5 leading-relaxed">{HINTS[kind]}</div>
              </div>
            );
          })()}
          {data?.enhancement?.status === "completed" && data?.enhancement?.result && (
            <section className="mb-12 print-section" data-testid="enhancement-block">
              <EnhancementSection enhancement={data.enhancement}/>
            </section>
          )}
          {!data?.enhancement && (
            <div className="mb-8 bg-[#141414] border border-[#0A84FF]/30 rounded-sm p-5 flex items-center gap-4 no-print" data-testid="enhance-cta-banner">
              <div className="w-10 h-10 rounded-sm border border-[#0A84FF]/40 bg-[#0A84FF]/10 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-[#0A84FF]" strokeWidth={1.8}/>
              </div>
              <div className="flex-1">
                <div className="kpi-label text-[#0A84FF]">/ AMPLIFY WITH PRIMARY DATA</div>
                <div className="text-sm text-white mt-0.5">Overlay your internal knowledge (past projects, relationships, testimonials) to get a hyper-personalised, tagged version of this report.</div>
              </div>
              <Button
                type="button"
                onClick={() => setEnhanceOpen(true)}
                data-testid="enhance-cta-btn"
                className="h-9 rounded-sm bg-[#0A84FF] hover:bg-[#409CFF] text-white gap-2 whitespace-nowrap"
              >
                <Sparkles className="w-3.5 h-3.5"/>
                <span className="text-xs font-mono-data tracking-wider uppercase">Enhance</span>
              </Button>
            </div>
          )}

          {/* 1. Executive Snapshot */}
          <Section id="s1" index={1} title="Executive Snapshot">
            <Subgrid>
              <SubCard title="Company Overview">
                <p className="text-sm text-[#E5E5E5] leading-relaxed">{exec.company_overview || "—"}</p>
              </SubCard>
              <SubCard title="Why This Account Matters">
                <p className="text-sm text-[#E5E5E5] leading-relaxed">{exec.why_account_matters || "—"}</p>
              </SubCard>
            </Subgrid>
            <Card className="p-5">
              <div className="kpi-label mb-3">/ TOP 3 STRATEGIC INSIGHTS</div>
              <BulletList items={exec.top_3_strategic_insights} testId="exec-insights"/>
            </Card>
            <Card className="p-5">
              <div className="kpi-label mb-2">/ ACCOUNT ATTRACTIVENESS SUMMARY</div>
              <p className="text-sm text-[#E5E5E5] leading-relaxed">{exec.account_attractiveness_summary || "—"}</p>
            </Card>
          </Section>

          {/* 2. Company Overview */}
          <Section id="s2" index={2} title="Company Overview">
            <Card className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
                <KV label="Legal Name" value={co.legal_name}/>
                <KV label="Year Founded" value={co.year_founded} mono/>
                <KV label="Employees" value={co.employee_strength} mono/>
                <KV label="Website" value={
                  co.website ? <a href={co.website.startsWith("http") ? co.website : `https://${co.website}`} target="_blank" rel="noreferrer" className="text-[#0A84FF] hover:underline inline-flex items-center gap-1">{co.website}<ExternalLink className="w-3 h-3"/></a> : null
                }/>
                <KV label="Global HQ" value={co.headquarters_global}/>
                <KV label="Profitability" value={co.profitability}/>
              </div>
              {co.regional_hqs && co.regional_hqs.length > 0 && (
                <div className="mt-3 pt-3 border-t border-[#1F1F1F]">
                  <div className="kpi-label mb-2">Regional HQs</div>
                  <div className="flex flex-wrap gap-2">
                    {co.regional_hqs.map((h, i) => <Pill key={`${h}-${i}`}>{h}</Pill>)}
                  </div>
                </div>
              )}
            </Card>
            <SubCard title="Revenue Trend (last 4 years)">
              <DataTable
                testId="revenue-table"
                columns={[
                  { key: "year", label: "Year", mono: true },
                  { key: "revenue", label: "Revenue", mono: true },
                  { key: "note", label: "Note" },
                ]}
                rows={co.revenue_trend || []}
              />
            </SubCard>
            <SubCard title="Estimated Spending Breakdown">
              <DataTable
                testId="spending-table"
                columns={[
                  { key: "area", label: "Area" },
                  { key: "amount", label: "Estimated Spend", mono: true, align: "right" },
                ]}
                rows={co.estimated_spending || []}
              />
            </SubCard>
            {co.assumptions && (
              <Card className="p-4 border-dashed">
                <div className="kpi-label mb-1 text-[#FF9F0A]">/ ASSUMPTIONS</div>
                <p className="text-sm text-[#A3A3A3]">{co.assumptions}</p>
              </Card>
            )}
          </Section>

          {/* 3. Business Structure */}
          <Section id="s3" index={3} title="Business Structure">
            <SubCard title="Business Units">
              <DataTable
                testId="business-units-table"
                columns={[
                  { key: "name", label: "Unit" },
                  { key: "revenue_pct", label: "Revenue %", mono: true, align: "right" },
                  { key: "products", label: "Key Products / Services" },
                  { key: "growth_areas", label: "Growth Areas" },
                ]}
                rows={bs.units || []}
              />
            </SubCard>
            <SubCard title="Geographic Revenue Split">
              <DataTable
                testId="geo-split-table"
                columns={[
                  { key: "region", label: "Region" },
                  { key: "pct", label: "Share", mono: true, align: "right" },
                ]}
                rows={bs.geographic_split || []}
              />
            </SubCard>
          </Section>

          {/* 4. Global Footprint */}
          <Section id="s4" index={4} title="Global Footprint">
            <Card className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="kpi-label mb-1">Manufacturing Plants</div>
                  <div className="font-mono-data text-3xl text-white flex items-center gap-2"><Building2 className="w-5 h-5 text-[#0A84FF]" strokeWidth={1.5}/>{gf.manufacturing_plants_count || "—"}</div>
                </div>
                <div>
                  <div className="kpi-label mb-1">GCCs</div>
                  <div className="font-mono-data text-3xl text-white flex items-center gap-2"><Globe className="w-5 h-5 text-[#0A84FF]" strokeWidth={1.5}/>{(gf.gccs || []).length}</div>
                </div>
                <div>
                  <div className="kpi-label mb-1">R&D Centers</div>
                  <div className="font-mono-data text-3xl text-white flex items-center gap-2"><Cpu className="w-5 h-5 text-[#0A84FF]" strokeWidth={1.5}/>{(gf.rd_centers || []).length}</div>
                </div>
                <div>
                  <div className="kpi-label mb-1">Plant Locations</div>
                  <div className="font-mono-data text-3xl text-white flex items-center gap-2"><MapPin className="w-5 h-5 text-[#0A84FF]" strokeWidth={1.5}/>{(gf.plant_locations || []).length}</div>
                </div>
              </div>
            </Card>
            {gf.summary && (
              <Card className="p-5">
                <div className="kpi-label mb-2">/ GLOBAL PRESENCE SUMMARY</div>
                <p className="text-sm text-[#E5E5E5] leading-relaxed">{gf.summary}</p>
              </Card>
            )}
            <SubCard title="Plant Locations">
              <DataTable
                testId="plants-table"
                columns={[
                  { key: "country", label: "Country" },
                  { key: "city", label: "City" },
                  { key: "function", label: "Function" },
                ]}
                rows={gf.plant_locations || []}
              />
            </SubCard>
            <Subgrid>
              <SubCard title="GCCs / Engineering Centers">
                <DataTable testId="gccs-table" columns={[{ key: "location", label: "Location" }, { key: "role", label: "Role" }]} rows={gf.gccs || []}/>
              </SubCard>
              <SubCard title="R&D Centers">
                <DataTable testId="rd-table" columns={[{ key: "location", label: "Location" }, { key: "role", label: "Role" }]} rows={gf.rd_centers || []}/>
              </SubCard>
            </Subgrid>
          </Section>

          {/* 5. Financial Insights */}
          <Section id="s5" index={5} title="Financial & Investment Insights">
            <SubCard title="CAPEX vs OPEX Trend">
              <p className="text-sm text-[#E5E5E5] leading-relaxed">{fin.capex_opex_trend || "—"}</p>
            </SubCard>
            <Subgrid>
              <SubCard title="Key Investment Areas"><BulletList items={fin.investment_areas} testId="invest-areas"/></SubCard>
              <SubCard title="Cost Pressures"><BulletList items={fin.cost_pressures} testId="cost-pressures"/></SubCard>
            </Subgrid>
            <SubCard title="Spending Account Indicators">
              <BulletList items={fin.spending_indicators} testId="spending-indicators"/>
            </SubCard>
          </Section>

          {/* 6. Strategic Priorities */}
          <Section id="s6" index={6} title="Strategic Priorities (3-5 Years)">
            <Subgrid>
              <SubCard title="Engineering Transformation"><BulletList items={sp.engineering_transformation}/></SubCard>
              <SubCard title="Manufacturing Modernization"><BulletList items={sp.manufacturing_modernization}/></SubCard>
              <SubCard title="Digital & AI Adoption"><BulletList items={sp.digital_ai_adoption}/></SubCard>
              <SubCard title="Innovation Themes"><BulletList items={sp.innovation_themes}/></SubCard>
            </Subgrid>
          </Section>

          {/* 7. Technology Landscape */}
          <Section id="s7" index={7} title="Technology Landscape">
            <DataTable
              testId="tech-table"
              columns={[
                { key: "category", label: "Category" },
                { key: "tools", label: "Tools / Platforms" },
                { key: "maturity", label: "Maturity", mono: true, align: "right" },
              ]}
              rows={tech}
            />
          </Section>

          {/* 8. Competitor Landscape */}
          <Section id="s8" index={8} title="Competitor Landscape">
            <Subgrid>
              <SubCard title="Major Global Competitors">
                <div className="flex flex-wrap gap-2">{(comp.global || []).map((c, i) => <Pill key={`${c}-${i}`}>{c}</Pill>) || "—"}</div>
              </SubCard>
              <SubCard title="Emerging / Niche Players">
                <div className="flex flex-wrap gap-2">{(comp.niche || []).map((c, i) => <Pill key={`${c}-${i}`} tone="blue">{c}</Pill>) || "—"}</div>
              </SubCard>
            </Subgrid>
          </Section>

          {/* 9. Partnerships */}
          <Section id="s9" index={9} title="Partnerships & Ecosystem">
            <Subgrid>
              <SubCard title="Technology Partners"><BulletList items={part.technology_partners}/></SubCard>
              <SubCard title="Strategic Alliances"><BulletList items={part.strategic_alliances}/></SubCard>
            </Subgrid>
            <SubCard title="Recent Collaborations"><BulletList items={part.recent_collaborations}/></SubCard>
          </Section>

          {/* 10. M&A */}
          <Section id="s10" index={10} title="Mergers & Acquisitions">
            <DataTable
              testId="ma-table"
              columns={[
                { key: "year", label: "Year", mono: true },
                { key: "target", label: "Target" },
                { key: "intent", label: "Strategic Intent" },
              ]}
              rows={ma}
            />
          </Section>

          {/* 11. Sustainability */}
          <Section id="s11" index={11} title="Sustainability & ESG">
            <Card className="p-5">
              <KV label="Net-Zero Targets" value={sus.net_zero_targets}/>
              <KV label="Progress vs Goals" value={sus.progress}/>
            </Card>
            <Subgrid>
              <SubCard title="Initiatives"><BulletList items={sus.initiatives}/></SubCard>
              <SubCard title="Bottlenecks"><BulletList items={sus.bottlenecks}/></SubCard>
            </Subgrid>
          </Section>

          {/* 12. Challenges */}
          <Section id="s12" index={12} title="Key Business & Technology Challenges" sub="Categorized across functions — actionable, not generic.">
            <Subgrid>
              <SubCard title="Engineering"><BulletList items={ch.engineering}/></SubCard>
              <SubCard title="Manufacturing"><BulletList items={ch.manufacturing}/></SubCard>
              <SubCard title="Supply Chain"><BulletList items={ch.supply_chain}/></SubCard>
              <SubCard title="After Sales"><BulletList items={ch.after_sales}/></SubCard>
            </Subgrid>
            <SubCard title="Digital & AI"><BulletList items={ch.digital_ai}/></SubCard>
          </Section>

          {/* 13. Outsourcing */}
          <Section id="s13" index={13} title="Outsourcing & Vendor Landscape">
            <Subgrid>
              <SubCard title="Key Engineering / IT Vendors">
                <div className="flex flex-wrap gap-2">{(out.key_vendors || []).map((v, i) => <Pill key={`${v}-${i}`}>{v}</Pill>)}</div>
              </SubCard>
              <SubCard title="Likely Outsourcing Areas"><BulletList items={out.likely_outsourcing}/></SubCard>
            </Subgrid>
            <SubCard title="High-Potential Outsourcing Opportunities">
              <BulletList items={out.high_potential} testId="high-potential-outsourcing"/>
            </SubCard>
          </Section>

          {/* 14. Leadership */}
          <Section id="s14" index={14} title="Key Leadership">
            <DataTable
              testId="leadership-table"
              columns={[
                { key: "role", label: "Role" },
                { key: "name", label: "Name" },
                { key: "priorities", label: "Likely Priorities" },
                { key: "linkedin", label: "LinkedIn / Search" },
              ]}
              rows={lead}
            />
          </Section>

          {/* 15. KPIs */}
          <Section id="s15" index={15} title="KPI & Performance Focus">
            <Subgrid>
              <SubCard title="Business KPIs"><BulletList items={kpi.business}/></SubCard>
              <SubCard title="Operational KPIs"><BulletList items={kpi.operational}/></SubCard>
            </Subgrid>
            <SubCard title="Digital KPIs"><BulletList items={kpi.digital}/></SubCard>
          </Section>

          {/* 16. Attractiveness Score */}
          <Section id="s16" index={16} title="Account Attractiveness Score">
            <AccountScoreGauge score={score.score} summary={score.reasoning}/>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="p-5">
                <div className="kpi-label mb-1 flex items-center gap-2"><TrendingUp className="w-3 h-3"/> Spending Potential</div>
                <p className="text-sm text-[#E5E5E5] mt-2">{score.spending_potential || "—"}</p>
              </Card>
              <Card className="p-5">
                <div className="kpi-label mb-1 flex items-center gap-2"><TrendingUp className="w-3 h-3"/> Growth Potential</div>
                <p className="text-sm text-[#E5E5E5] mt-2">{score.growth_potential || "—"}</p>
              </Card>
              <Card className="p-5">
                <div className="kpi-label mb-1">Ease of Entry</div>
                <p className="text-sm text-[#E5E5E5] mt-2">{score.ease_of_entry || "—"}</p>
              </Card>
              <Card className="p-5">
                <div className="kpi-label mb-1 flex items-center gap-2"><Trophy className="w-3 h-3"/> Strategic Fit</div>
                <p className="text-sm text-[#E5E5E5] mt-2">{score.strategic_fit || "—"}</p>
              </Card>
            </div>
          </Section>

          {/* 17. Deal Opportunities */}
          <Section id="s17" index={17} title="High-Value Deal Opportunities">
            <DataTable
              testId="deals-table"
              columns={[
                { key: "area", label: "Area" },
                { key: "problem", label: "Problem" },
                { key: "solution", label: "Proposed Solution" },
                { key: "deal_size", label: "Deal Size", mono: true, align: "right" },
                { key: "why_buy", label: "Why They'll Buy" },
                { key: "persona", label: "Target Persona" },
              ]}
              rows={deals}
            />
          </Section>

          {/* 18. CXO Email */}
          <Section id="s18" index={18} title="CXO-Level Outreach Email">
            <CXOEmail email={email}/>
          </Section>

          {/* 19. PPT Storyline */}
          <Section id="s19" index={19} title="PPT Storyline (Sales Meeting)">
            <PPTStoryline slides={ppt}/>
          </Section>

          {/* 20. Pipeline Tracker */}
          <Section id="s20" index={20} title="Pipeline Tracker (CRM-Lite)" sub="Edit and save — synced to your account intelligence library.">
            <PipelineTracker reportId={data.id} accountName={data.company_name} initial={pipe}/>
          </Section>

          {/* 21. Data Confidence */}
          <Section id="s21" index={21} title="Data Confidence & Sources">
            <Card className="p-5">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="kpi-label">Confidence Level</div>
                <Pill tone={conf.confidence_level === "High" ? "green" : conf.confidence_level === "Low" ? "red" : "amber"}>
                  {conf.confidence_level || "Medium"}
                </Pill>
                {web.grounded && (
                  <>
                    <div className="kpi-label ml-4">Grounding</div>
                    <Pill tone="blue">Live web · {webSources.length} sources · {web.searches_completed}/{(web.searches_completed || 0) + (web.searches_failed || 0)} searches</Pill>
                  </>
                )}
              </div>
            </Card>
            <Subgrid>
              <SubCard title="Assumptions Made"><BulletList items={conf.assumptions}/></SubCard>
              <SubCard title="Suggested Sources for Validation">
                <ul className="space-y-1.5">
                  {(conf.sources || []).map((s, i) => (
                    <li key={`${s}-${i}`} className="text-sm text-[#E5E5E5] flex items-start gap-2">
                      <span className="kpi-label text-[#525252] w-6 mt-0.5">{String(i + 1).padStart(2, "0")}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </SubCard>
            </Subgrid>

            {web.grounded && webSources.length > 0 && (
              <Card className="p-5 mt-4" data-testid="live-web-sources-card">
                <div className="kpi-label mb-3">/ LIVE WEB SOURCES (powered by Tavily)</div>
                <div className="space-y-4">
                  {Object.keys(DIM_LABELS).filter(k => (grouped[k] || []).length > 0).map(dim => (
                    <div key={dim}>
                      <div className="text-[11px] font-mono-data tracking-wider uppercase text-[#0A84FF] mb-2">
                        {DIM_LABELS[dim]} · {grouped[dim].length}
                      </div>
                      <ul className="space-y-1.5">
                        {grouped[dim].map((s, i) => (
                          <li key={s.url || `${dim}-${i}`} className="text-sm text-[#E5E5E5] flex items-start gap-2 leading-relaxed">
                            <span className="kpi-label text-[#525252] w-6 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                            <div className="min-w-0 flex-1">
                              <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#E5E5E5] hover:text-[#0A84FF] transition-colors inline-flex items-center gap-1.5 break-words"
                                data-testid={`web-source-link-${dim}-${i}`}
                              >
                                <span className="underline-offset-2 hover:underline">{s.title || s.domain}</span>
                                <ExternalLink className="w-3 h-3 shrink-0 opacity-60"/>
                              </a>
                              <div className="text-[11px] font-mono-data text-[#525252] mt-0.5">{s.domain}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </Section>

          <div className="border-t border-[#262626] mt-6 pt-6 text-center kpi-label">
            / END OF REPORT · {data.company_name.toUpperCase()} · RADAR · {new Date(data.created_at).toLocaleDateString()}
          </div>
        </main>
      </div>

      <EnhanceDialog
        open={enhanceOpen}
        onOpenChange={setEnhanceOpen}
        reportId={id}
        initial={data?.enhancement?.primary_data}
        onStarted={onEnhanceStarted}
      />

      <UpgradeModal
        open={!!upgrade}
        onClose={() => setUpgrade(null)}
        resource={upgrade?.resource || "downloads"}
        plan={upgrade?.plan}
        used={upgrade?.used}
        limit={upgrade?.limit}
      />
    </div>
  );
}
